import { Component, HostBinding, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ClaimItemType } from 'src/app/types/claim-item.type';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DurationPipe } from '@pipes/duration.pipe';
import { Learner, Session, SessionServicesEnum } from 'src/app/queries/generated';
import { SessionsService } from '@services/sessions.service';

@Component({
  selector: 'lv-claims-list-item',
  templateUrl: './claims-list-item.component.html',
  styleUrls: ['claims-list-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, TranslateModule, RouterLink, DurationPipe]
})
export class ClaimsListItemComponent implements OnInit, OnDestroy {
  private sessionsService = inject(SessionsService);
  private translate = inject(TranslateService);

  @Input() session!: Session;
  @Input() claimItemType!: ClaimItemType;

  waitTime: number;
  shuttingDown: boolean;
  learner: Learner;

  @HostBinding('class.is-claimed') get isClaimed(): boolean {
    return !!this.session.claimedBy;
  }

  @HostBinding('class.is-live') get isLive(): boolean {
    return this.session.services.includes(SessionServicesEnum.LiveIdVerification);
  }

  @HostBinding('class.is-finished') get isFinished(): boolean {
    return !!this.session.finishedAt;
  }

  ngOnInit() {
    this.shuttingDown = false;
    this.learner = this.session.learner;

    this.updateWaitTime();
  }

  ngOnDestroy() {
    this.shuttingDown = true;
  }

  updateWaitTime() {
    if (this.shuttingDown) {
      return;
    }

    requestAnimationFrame(() => this.updateWaitTime());

    this.waitTime = Date.now() - this.originWaitingTime().getTime();
  }

  claim() {
    this.sessionsService.claimSession({ sessionId: this.session.id }).subscribe();
  }

  services() {
    return this.session.services.map((service) => this.translate.instant(`session._service.${service}`)).join(', ');
  }

  /**
   * If it is not a retakeRequest, then we return the createdAt of the session.
   * If it is a retakeRequest
   *  - returns the retakenAt when the retakeRequest is retaken
   *  - returns the createdAt if it is pending (not retaken yet)
   */
  private originWaitingTime(): Date {
    const retakeRequest = this.session.retakeRequest;
    let response = this.session.createdAt;

    if (this.claimItemType === 'retakeRequest') {
      response = retakeRequest.retakenAt || retakeRequest.createdAt;
    }

    return new Date(response);
  }
}
